const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class AccessibleDisclosure extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[aria-controls]');
    this.panel = this.trigger
      ? document.getElementById(this.trigger.getAttribute('aria-controls'))
      : null;

    if (!this.trigger || !this.panel) return;

    this.trigger.addEventListener('click', () => this.toggle());
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
        this.trigger.focus();
      }
    });
  }

  get isOpen() {
    return this.trigger.getAttribute('aria-expanded') === 'true';
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.trigger.setAttribute('aria-expanded', 'true');
    this.panel.hidden = false;

    const focusTarget = this.panel.querySelector('input, button, a[href], select, textarea');
    if (focusTarget) {
      focusTarget.focus();
    }
  }

  close() {
    this.trigger.setAttribute('aria-expanded', 'false');
    this.panel.hidden = true;
  }
}

class ModalMenu extends HTMLElement {
  connectedCallback() {
    this.trigger = document.querySelector(`[aria-controls="${this.id}"]`);
    this.closeButtons = [...this.querySelectorAll('[data-menu-close]')];

    if (!this.trigger || this.closeButtons.length === 0) return;

    this.trigger.addEventListener('click', () => this.open());
    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.close());
    });
    this.addEventListener('keydown', (event) => this.handleKeydown(event));
  }

  open() {
    this.hidden = false;
    document.body.classList.add('is-scroll-locked');
    this.trigger.setAttribute('aria-expanded', 'true');

    const preferredFocus =
      this.querySelector('.mobile-menu__identify') ||
      this.querySelector('[data-menu-close]') ||
      this.querySelector(focusableSelector);

    if (preferredFocus) preferredFocus.focus();
  }

  close() {
    this.hidden = true;
    document.body.classList.remove('is-scroll-locked');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.focus();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = [...this.querySelectorAll(focusableSelector)].filter(
      (element) => !element.hasAttribute('disabled') && element.getAttribute('tabindex') !== '-1' && element.offsetParent !== null,
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

function syncHeaderHeight() {
  const header = document.querySelector('[data-site-header]');
  if (!header) return;

  const height = Math.ceil(header.getBoundingClientRect().height);
  document.documentElement.style.setProperty('--header-height', `${height}px`);
}

function initStickyHeader() {
  const header = document.querySelector('[data-site-header]');
  if (!header) return;

  const update = () => {
    const scrolled = window.scrollY > 8;
    header.classList.toggle('site-header--scrolled', scrolled);
  };

  update();
  syncHeaderHeight();

  window.addEventListener(
    'scroll',
    () => {
      update();
    },
    { passive: true },
  );

  window.addEventListener('resize', () => {
    syncHeaderHeight();
  });

  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver(() => syncHeaderHeight());
    observer.observe(header);
  }
}

document.documentElement.dataset.reduceMotion = reduceMotion ? 'true' : 'false';

if (!customElements.get('accessible-disclosure')) {
  customElements.define('accessible-disclosure', AccessibleDisclosure);
}
if (!customElements.get('modal-menu')) {
  customElements.define('modal-menu', ModalMenu);
}

initStickyHeader();
initGoogleAdsOffer();

function initGoogleAdsOffer() {
  const bar = document.querySelector('[data-google-ads-promo]');
  if (!bar) return;

  try {
    const params = new URLSearchParams(window.location.search);
    const source = (params.get('utm_source') || '').toLowerCase();
    const medium = (params.get('utm_medium') || '').toLowerCase();
    const campaign = (params.get('utm_campaign') || '').toLowerCase();
    const fromGoogleAds =
      (source === 'google' && (medium === 'cpc' || medium === 'ppc' || medium === 'paid')) ||
      campaign.includes('summer10');

    if (fromGoogleAds) {
      sessionStorage.setItem('tsw_google_ads_offer', '1');
    }

    if (sessionStorage.getItem('tsw_google_ads_offer') !== '1') return;

    bar.hidden = false;
    const header = document.querySelector('[data-site-header]');
    if (header) header.classList.add('site-header--has-promo');
    syncHeaderHeight();
  } catch (error) {
    bar.hidden = true;
  }
}

function initCollectionSort() {
  const selects = document.querySelectorAll('[data-collection-sort]');
  if (selects.length === 0) return;

  // Sorting is applied by the explicit submit control only. Submitting on `change`
  // would move the customer to a new page while they are still browsing options
  // (WCAG 2.2 SC 3.2.2 On Input).

  if (window.location.hash !== '#CollectionProducts') return;

  const results = document.getElementById('CollectionProducts');
  if (results) {
    results.focus({ preventScroll: reduceMotion });
  }
}

initCollectionSort();

function groupThousands(value, separator) {
  return value.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

function formatMoney(cents, format) {
  const value = Number(cents) / 100;
  const [whole, fraction] = value.toFixed(2).split('.');
  // Liquid's money filters group thousands. Without matching that here, switching
  // variant on a product over 999.99 would render a different price to the server.
  const amount = `${groupThousands(whole, ',')}.${fraction}`;
  const amountNoDecimals = groupThousands(String(Math.round(value)), ',');
  const amountWithComma = `${groupThousands(whole, '.')},${fraction}`;

  if (!format) return `£${amount}`;
  return format
    .replace(/\{\{\s*amount\s*\}\}/g, amount)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, groupThousands(String(Math.round(value)), '.'));
}

function initProductForm() {
  const form = document.querySelector('[data-product-form]');
  if (!form) return;

  // Live OPC cut-to-size uses data-cut-to-size-product / -price / -form.
  // data-cut-to-size alone is only present when the theme calculator snippet mounts.
  const isCutToSize = Boolean(
    document.querySelector(
      '[data-cut-to-size], [data-cut-to-size-product], [data-cut-to-size-price], form[data-cut-to-size-form="true"]'
    )
  );
  const jsonEl = form.querySelector('[data-product-json]');
  // Live OPC cut-to-size: visible Thickness sits above OPC; hidden name=id stays in-form
  // so OPC can detect form[action*=/cart/add] input[name=id] for hydration.
  const thicknessSource = document.querySelector('[data-cut-thickness-source]');
  const thicknessTarget = form.querySelector('[data-cut-thickness-target]');
  if (thicknessSource && thicknessTarget) {
    const syncThickness = () => {
      thicknessTarget.value = thicknessSource.value;
      thicknessTarget.dispatchEvent(new Event('change', { bubbles: true }));
    };
    thicknessSource.addEventListener('change', syncThickness);
    syncThickness();
  }
  const variantInput =
    form.querySelector('[data-product-variant]') ||
    document.querySelector('[data-cut-thickness-source]') ||
    form.elements.namedItem('id');
  const priceBlock = document.querySelector('[data-product-price-block]');
  const availability = document.querySelector('[data-product-availability]');
  const addButton = form.querySelector('[data-add-to-basket]');
  const stickyAdd = document.querySelector('[data-sticky-add]');
  const stickyPrice = document.querySelector('[data-sticky-price]');
  const status = form.querySelector('[data-product-form-status]');
  const moneyFormat = form.getAttribute('data-money-format') || '';

  if (!jsonEl || !variantInput) return;

  let variants;
  try {
    variants = JSON.parse(jsonEl.textContent);
  } catch (error) {
    return;
  }

  if (!Array.isArray(variants)) return;

  const soldOut = 'Sold out';
  const inStock = 'In stock';
  const enterMeasurementsPrice = 'Enter your measurements to calculate your price';

  const update = () => {
    const variantId = Number(variantInput.value);
    const variant = variants.find((entry) => entry.id === variantId);
    if (!variant) return;

    if (!isCutToSize && priceBlock) {
      const current = priceBlock.querySelector('[data-price-current]');
      const compare = priceBlock.querySelector('[data-price-compare]');
      const compareValue = priceBlock.querySelector('[data-price-compare-value]');
      if (current) current.textContent = formatMoney(variant.price, moneyFormat);
      if (compare && compareValue) {
        if (variant.compare_at_price > variant.price) {
          compare.hidden = false;
          compareValue.textContent = formatMoney(variant.compare_at_price, moneyFormat);
        } else {
          compare.hidden = true;
          compareValue.textContent = '';
        }
      }
    }

    if (isCutToSize) {
      // Keep Shopify £0.00 variant prices off the PDP / sticky bar on this product only.
      const current = priceBlock?.querySelector('[data-price-current]');
      if (current) current.textContent = enterMeasurementsPrice;
      if (stickyPrice) stickyPrice.textContent = enterMeasurementsPrice;
    }

    if (availability) {
      availability.textContent = variant.available ? inStock : soldOut;
    }

    const flavourGallery = document.querySelector('[data-flavour-gallery]');
    if (flavourGallery && variant.featured_media_id != null) {
      const mediaId = String(variant.featured_media_id);
      flavourGallery.querySelectorAll('[data-flavour-media]').forEach((figure) => {
        const match = String(figure.getAttribute('data-media-id')) === mediaId;
        figure.hidden = !match;
        figure.classList.toggle('product-gallery__item--primary', match);
        if (match) {
          const img = figure.querySelector('img');
          if (img) {
            img.loading = 'eager';
            if (!img.complete || img.naturalWidth === 0) {
              const src = img.currentSrc || img.src;
              if (src) img.src = src;
            }
          }
        }
      });
      flavourGallery.querySelectorAll('[data-flavour-thumb]').forEach((thumb) => {
        const match = String(thumb.getAttribute('data-media-id')) === mediaId;
        thumb.classList.toggle('is-active', match);
      });
    }

    const artSetGalleries = document.querySelectorAll('[data-art-set-gallery]');
    if (artSetGalleries.length) {
      const setName = variant.option1 || variant.title || '';
      artSetGalleries.forEach((gallery) => {
        const match = gallery.getAttribute('data-art-set-gallery') === setName;
        gallery.hidden = !match;
        gallery.querySelectorAll('video').forEach((video) => {
          if (!match) {
            video.pause();
          }
        });
        if (match) {
          const items = gallery.querySelectorAll('.product-gallery__item');
          items.forEach((item, index) => {
            item.classList.toggle('product-gallery__item--primary', index === 0);
          });
        }
      });
      const sizeEl = document.querySelector('[data-art-set-size]');
      if (sizeEl) sizeEl.textContent = setName;
      document.querySelectorAll('[data-art-set-panel]').forEach((panel) => {
        panel.hidden = panel.getAttribute('data-art-set-panel') !== setName;
      });
    }

    const decorativePatternEl = document.querySelector('[data-decorative-pattern-name]');
    if (decorativePatternEl) {
      const selectedOption = variantInput.options?.[variantInput.selectedIndex];
      const patternName = selectedOption?.getAttribute('data-pattern-name') || '';
      decorativePatternEl.textContent = patternName;
      const patternWrap = decorativePatternEl.closest('.decorative-selected-pattern');
      if (patternWrap) patternWrap.hidden = !patternName;
    }

    const packSummary = document.querySelector('[data-clay-pack-summary]');
    const perBrick = document.querySelector('[data-clay-per-brick]');
    if (packSummary) {
      const selectedOption = variantInput.options?.[variantInput.selectedIndex];
      const brickCount = Number.parseInt(selectedOption?.getAttribute('data-brick-count') || '', 10);
      const priceLabel = formatMoney(variant.price, moneyFormat);
      if (Number.isInteger(brickCount) && brickCount > 0 && Number.isFinite(variant.price)) {
        packSummary.textContent = `${brickCount} bricks — ${priceLabel}`;
        packSummary.hidden = false;
        if (perBrick) {
          const unitCents = Math.round(variant.price / brickCount);
          if (Number.isFinite(unitCents) && unitCents > 0) {
            perBrick.textContent = `${formatMoney(unitCents, moneyFormat)} per brick`;
            perBrick.hidden = false;
          } else {
            perBrick.textContent = '';
            perBrick.hidden = true;
          }
        }
      } else {
        packSummary.hidden = true;
        if (perBrick) {
          perBrick.textContent = '';
          perBrick.hidden = true;
        }
      }
    }

    const fibreSummary = document.querySelector('[data-fibre-roll-summary]');
    if (fibreSummary) {
      const selectedOption = variantInput.options?.[variantInput.selectedIndex];
      const rollSummary = selectedOption?.getAttribute('data-roll-summary') || '';
      if (rollSummary) {
        fibreSummary.textContent = rollSummary;
        fibreSummary.hidden = false;
      }
    }

    const calciumSummary = document.querySelector('[data-calcium-summary]');
    if (calciumSummary) {
      const selectedOption = variantInput.options?.[variantInput.selectedIndex];
      const calciumLine = selectedOption?.getAttribute('data-calcium-summary') || '';
      if (calciumLine) {
        calciumSummary.textContent = calciumLine;
        calciumSummary.hidden = false;
      }
    }

    const flavourStoryRoot = document.querySelector('[data-chips-flavour-story]');
    const flavourStoriesEl = document.querySelector('[data-chips-flavour-stories]');
    if (flavourStoryRoot && flavourStoriesEl) {
      let stories = null;
      try {
        stories = JSON.parse(flavourStoriesEl.textContent);
      } catch (error) {
        stories = null;
      }
      const entry = stories && variant.title ? stories[variant.title] : null;
      if (entry) {
        const nameEl = flavourStoryRoot.querySelector('[data-chips-flavour-name]');
        const copyEl = flavourStoryRoot.querySelector('[data-chips-flavour-copy]');
        const pairsEl = flavourStoryRoot.querySelector('[data-chips-flavour-pairs]');
        const smokeEl = flavourStoryRoot.querySelector('[data-chips-smoke]');
        if (nameEl) nameEl.textContent = entry.name;
        if (copyEl) copyEl.textContent = entry.story;
        if (pairsEl && entry.pairs) {
          pairsEl.innerHTML = '';
          entry.pairs.split('•').forEach((item) => {
            const tag = item.trim();
            if (!tag) return;
            const span = document.createElement('span');
            span.className = 'chips-34l-tag';
            span.textContent = tag;
            pairsEl.appendChild(span);
          });
        }
        if (smokeEl && entry.smoke) {
          const level = Number(entry.smoke) || 0;
          smokeEl.setAttribute('data-smoke', String(level));
          smokeEl.setAttribute('aria-label', `Smoke strength ${level} of 5`);
          smokeEl.querySelectorAll('.chips-34l-smoke__flame').forEach((flame, index) => {
            flame.classList.toggle('is-on', index < level);
          });
        }
      }
    }

    if (!isCutToSize) {
      [addButton, stickyAdd].forEach((button) => {
        if (!button) return;
        button.disabled = !variant.available;
        button.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
      });

      if (stickyPrice) {
        stickyPrice.textContent = formatMoney(variant.price, moneyFormat);
      }

      if (status) {
        status.textContent = variant.available
          ? `Selected option updated. ${formatMoney(variant.price, moneyFormat)}.`
          : 'Selected option is sold out.';
      }
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    if (document.querySelector('[data-art-set-gallery]')) {
      url.hash = '';
    }
    window.history.replaceState({}, '', url);

    document.querySelectorAll('[data-rc-pack]').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.getAttribute('data-rc-pack') === String(variant.id) ? 'true' : 'false');
    });
  };

  variantInput.addEventListener('change', update);

  document.querySelectorAll('[data-art-set-gallery]').forEach((gallery) => {
    gallery.querySelectorAll('[data-art-set-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        const id = (thumb.getAttribute('href') || '').replace('#', '');
        const item = id ? document.getElementById(id) : null;
        if (!item || !gallery.contains(item)) return;
        gallery.querySelectorAll('.product-gallery__item').forEach((figure) => {
          figure.classList.toggle('product-gallery__item--primary', figure === item);
        });
        gallery.querySelectorAll('video').forEach((video) => {
          if (!item.contains(video)) video.pause();
        });
      });
    });
  });

  document.querySelectorAll('[data-rc-pack]').forEach((btn) => {
    btn.addEventListener('click', () => {
      variantInput.value = btn.getAttribute('data-rc-pack');
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  const flavourGallery = document.querySelector('[data-flavour-gallery]');
  if (flavourGallery && variantInput.tagName === 'SELECT') {
    flavourGallery.querySelectorAll('[data-flavour-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        const variantId = thumb.getAttribute('data-variant-id');
        if (variantId) {
          variantInput.value = variantId;
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
        const mediaId = String(thumb.getAttribute('data-media-id') || '');
        flavourGallery.querySelectorAll('[data-flavour-media]').forEach((figure) => {
          const match = String(figure.getAttribute('data-media-id')) === mediaId;
          figure.hidden = !match;
          figure.classList.toggle('product-gallery__item--primary', match);
        });
        flavourGallery.querySelectorAll('[data-flavour-thumb]').forEach((other) => {
          other.classList.toggle('is-active', other === thumb);
        });
      });
    });
  }

  const sticky = document.querySelector('[data-sticky-atc]');
  const summary = document.querySelector('.product-summary');
  const stickyQuery = window.matchMedia('(max-width: 47.99rem)');

  if (sticky && summary && typeof IntersectionObserver !== 'undefined') {
    const syncSticky = (entry) => {
      const shouldShow = stickyQuery.matches && entry && !entry.isIntersecting;
      sticky.hidden = !shouldShow;
    };

    let latestEntry = { isIntersecting: true };
    const observer = new IntersectionObserver(
      ([entry]) => {
        latestEntry = entry;
        syncSticky(entry);
      },
      { rootMargin: '-12% 0px 0px 0px', threshold: 0 },
    );
    observer.observe(summary);

    const onBreakpointChange = () => {
      if (!stickyQuery.matches) {
        sticky.hidden = true;
        return;
      }
      syncSticky(latestEntry);
    };

    if (typeof stickyQuery.addEventListener === 'function') {
      stickyQuery.addEventListener('change', onBreakpointChange);
    } else if (typeof stickyQuery.addListener === 'function') {
      stickyQuery.addListener(onBreakpointChange);
    }
  }

  if (document.querySelector('[data-art-set-gallery]')) {
    update();
  }
}

initProductForm();

function initCutToSizeCalculator() {
  const root = document.querySelector('[data-cut-to-size]');
  const form = document.querySelector('[data-product-form]');
  if (!root || !form) return;

  const heightInput = root.querySelector('[data-cut-height]');
  const widthInput = root.querySelector('[data-cut-width]');
  const result = root.querySelector('[data-cut-result]');
  const priceProperty = root.querySelector('[data-cut-price-property]');
  const variantInput =
    form.querySelector('[data-product-variant]') ||
    document.querySelector('[data-cut-thickness-source]') ||
    form.elements.namedItem('id');
  const addButton = form.querySelector('[data-add-to-basket]');
  const stickyAdd = document.querySelector('[data-sticky-add]');
  const stickyPrice = document.querySelector('[data-sticky-price]');
  const priceCurrent = document.querySelector('[data-cut-to-size-price] [data-price-current]');
  const status = form.querySelector('[data-product-form-status]');
  const moneyFormat = root.getAttribute('data-money-format') || form.getAttribute('data-money-format') || '';

  const rates = {
    '25': Number(root.getAttribute('data-rate-25') || 0),
    '30': Number(root.getAttribute('data-rate-30') || 0),
    '40': Number(root.getAttribute('data-rate-40') || 0),
  };
  const maxWidth = Number(root.getAttribute('data-max-width') || 610);
  const maxHeight = Number(root.getAttribute('data-max-height') || 500);
  const minMm = Number(root.getAttribute('data-min-mm') || 50);
  const ratesReady = root.getAttribute('data-rates-ready') === 'true';

  const thicknessFromVariant = () => {
    if (!variantInput) return '25';
    if (variantInput.tagName === 'SELECT') {
      const option = variantInput.options[variantInput.selectedIndex];
      const raw = (option && (option.getAttribute('data-thickness') || option.textContent)) || '';
      const match = String(raw).match(/(\d+)\s*mm/i);
      return match ? match[1] : '25';
    }
    return '25';
  };

  const setButtonsEnabled = (enabled) => {
    [addButton, stickyAdd].forEach((button) => {
      if (!button) return;
      button.disabled = !enabled;
      button.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    });
  };

  const update = () => {
    const height = Number(heightInput && heightInput.value);
    const width = Number(widthInput && widthInput.value);
    const thickness = thicknessFromVariant();
    const rate = rates[thickness] || 0;

    if (!height || !width) {
      if (result) result.textContent = 'Enter height and width to calculate your price.';
      if (priceProperty) priceProperty.value = '';
      if (priceCurrent) priceCurrent.textContent = 'Enter your measurements to calculate your price.';
      if (stickyPrice) stickyPrice.textContent = 'Enter your measurements to calculate your price.';
      if (status) status.textContent = 'Enter your measurements to calculate your price before adding to the basket.';
      setButtonsEnabled(false);
      return;
    }

    if (height < minMm || width < minMm || height > maxHeight || width > maxWidth) {
      if (result) {
        result.textContent = `Measurements must be between ${minMm}mm and ${maxHeight}mm (height) / ${maxWidth}mm (width).`;
      }
      if (priceProperty) priceProperty.value = '';
      setButtonsEnabled(false);
      return;
    }

    if (!ratesReady || rate <= 0) {
      if (result) {
        result.textContent =
          'Measurements look valid. Pricing rates still need confirming — contact us with these sizes to complete the order. This board is not free.';
      }
      if (priceProperty) priceProperty.value = 'To be confirmed';
      if (priceCurrent) priceCurrent.textContent = 'Price on confirmation';
      if (stickyPrice) stickyPrice.textContent = 'Price on confirmation';
      if (status) {
        status.textContent =
          'Measurements saved. Contact us to confirm cutting and price — the board is not free.';
      }
      setButtonsEnabled(false);
      return;
    }

    const areaM2 = (height * width) / 1000000;
    let pounds = areaM2 * rate;
    const minCharge = Number(root.getAttribute('data-min-charge') || 0);
    if (minCharge > 0 && pounds < minCharge) pounds = minCharge;
    const pence = Math.round(pounds * 100);
    const formatted = formatMoney(pence, moneyFormat);

    if (result) {
      result.textContent = `Calculated price for ${height}mm × ${width}mm × ${thickness}mm: ${formatted}`;
    }
    if (priceProperty) priceProperty.value = formatted;
    if (priceCurrent) priceCurrent.textContent = formatted;
    if (stickyPrice) stickyPrice.textContent = formatted;
    if (status) {
      status.textContent = `Price calculated: ${formatted}. Review carefully, then add to basket.`;
    }
    setButtonsEnabled(true);
  };

  ['input', 'change'].forEach((eventName) => {
    if (heightInput) heightInput.addEventListener(eventName, update);
    if (widthInput) widthInput.addEventListener(eventName, update);
  });
  if (variantInput) variantInput.addEventListener('change', update);

  form.addEventListener('submit', (event) => {
    update();
    if (addButton && addButton.disabled) {
      event.preventDefault();
      if (status) {
        status.textContent = 'Enter valid measurements to calculate your price before adding to the basket.';
      }
    }
  });

  update();
}

initCutToSizeCalculator();

/**
 * Cart-page measurement acknowledgement for cut-to-size lines only.
 * Blocks Checkout until checked; does not block Update basket.
 * Does not touch OPC / product-page Add to basket.
 */
function initCartCutToSizeAcknowledgement() {
  const form = document.querySelector('[data-cart-form][data-cart-has-cut-to-size]');
  if (!form) return;

  const ack = form.querySelector('[data-cart-cts-ack]');
  const error = form.querySelector('[data-cart-cts-ack-error]');
  const checkoutBtn = form.querySelector('button[name="checkout"]');
  if (!ack || !checkoutBtn) return;

  const showError = () => {
    if (error) {
      error.hidden = false;
    }
    ack.setAttribute('aria-invalid', 'true');
    ack.focus({ preventScroll: false });
  };

  const clearError = () => {
    if (error) error.hidden = true;
    ack.removeAttribute('aria-invalid');
  };

  const syncCheckout = () => {
    if (checkoutBtn.hasAttribute('data-cts-price-blocked')) return;
    const allowed = ack.checked;
    checkoutBtn.disabled = !allowed;
    if (allowed) {
      checkoutBtn.removeAttribute('aria-disabled');
      clearError();
    } else {
      checkoutBtn.setAttribute('aria-disabled', 'true');
    }
  };

  ack.addEventListener('change', syncCheckout);
  ack.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    // Native checkbox handles Space; Enter toggles for keyboard clarity.
    if (event.key === 'Enter') {
      event.preventDefault();
      ack.checked = !ack.checked;
      ack.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  form.addEventListener('submit', (event) => {
    const submitter = event.submitter;
    if (submitter && submitter.getAttribute('name') === 'update') return;
    if (checkoutBtn.hasAttribute('data-cts-price-blocked')) {
      event.preventDefault();
      return;
    }
    const isCheckout =
      (submitter && submitter.getAttribute('name') === 'checkout') ||
      (!submitter && document.activeElement === checkoutBtn);
    if (!isCheckout && submitter) return;
    if (ack.checked) return;
    event.preventDefault();
    showError();
  });

  syncCheckout();
}

initCartCutToSizeAcknowledgement();

/**
 * Cut-to-size PDP only: hide Buy it now / Shop Pay / dynamic checkout if injected
 * (theme intentionally omits payment_button; OPC or apps may still inject).
 */
function suppressCutToSizeAcceleratedCheckout() {
  const isCutToSize =
    document.body.classList.contains('tsw-cut-to-size-product') ||
    Boolean(document.querySelector('[data-cut-to-size-product]'));
  if (!isCutToSize) return;

  const hide = (node) => {
    if (!node || node.getAttribute('data-tsw-cts-accel-hidden') === 'true') return;
    node.setAttribute('data-tsw-cts-accel-hidden', 'true');
    node.setAttribute('hidden', 'hidden');
    node.style.setProperty('display', 'none', 'important');
    node.setAttribute('aria-hidden', 'true');
  };

  const sweep = () => {
    document
      .querySelectorAll(
        '.shopify-payment-button, .additional-checkout-buttons, [data-shopify-buttoncontainer], accelerated-checkout, .dynamic-checkout__content, .shopify-payment-button__button'
      )
      .forEach(hide);
  };

  sweep();
  setTimeout(sweep, 500);
  setTimeout(sweep, 2000);
  setTimeout(sweep, 5000);
}

suppressCutToSizeAcceleratedCheckout();

/**
 * Cut-to-size only: hide OPC "Final price: £0.00" until a real calculated amount exists.
 * Does not change calculator pricing — display only.
 */
function hideCutToSizeZeroFinalPrice() {
  const isCutToSize =
    document.body.classList.contains('tsw-cut-to-size-product') ||
    Boolean(document.querySelector('[data-cut-to-size-product]'));
  if (!isCutToSize) return;

  const sync = () => {
    document.querySelectorAll('.final_price_wrapper').forEach((wrap) => {
      const el = wrap.querySelector('.final_price_el') || wrap;
      const text = (el.textContent || '').replace(/\s+/g, ' ');
      const match = text.match(/£\s*([\d,.]+)/);
      const amount = match ? Number(String(match[1]).replace(/,/g, '')) : NaN;
      const isZero = Number.isFinite(amount) && amount === 0;
      wrap.hidden = isZero;
      wrap.style.setProperty('display', isZero ? 'none' : '', 'important');
    });
  };

  sync();
  const root = document.querySelector('.calculator-form-container, [data-cut-to-size-product]');
  if (root && typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(sync);
    observer.observe(root, { subtree: true, childList: true, characterData: true });
  }
  document.addEventListener('input', sync, true);
  document.addEventListener('change', sync, true);
}

hideCutToSizeZeroFinalPrice();

/**
 * Payment-path guards for live OPC cut-to-size:
 * - block rapid double Add-to-basket (unique OPC draft tokens otherwise create duplicate lines)
 * - block ATC when Width/Height fail native min/max (OPC can still display a price)
 */
function initCutToSizePaymentGuards() {
  const form = document.querySelector('form[data-product-form][data-cut-to-size-form="true"]');
  if (!form && !document.querySelector('[data-cut-to-size-price]')) return;

  let submitting = false;
  const LOCK_MS = 5000;

  const readDims = () => {
    const width = document.querySelector('input[name="width"]');
    const height = document.querySelector('input[name="height"]');
    if (!width || !height) return { ok: true };
    const w = Number(width.value);
    const h = Number(height.value);
    const minW = Number(width.min || 50);
    const maxW = Number(width.max || 610);
    const minH = Number(height.min || 50);
    const maxH = Number(height.max || 500);
    if (!Number.isFinite(w) || !Number.isFinite(h) || !w || !h) {
      return { ok: false, message: 'Enter width and height to calculate your price before adding to the basket.' };
    }
    if (w < minW || w > maxW || h < minH || h > maxH) {
      return {
        ok: false,
        message: `Measurements must be between ${minW}–${maxW}mm (width) and ${minH}–${maxH}mm (height).`,
      };
    }
    return { ok: true };
  };

  const setStatus = (message) => {
    const status = document.querySelector('[data-product-form-status]');
    if (status) status.textContent = message;
  };

  const lockAddButtons = () => {
    document.querySelectorAll('button, [role="button"]').forEach((button) => {
      if (!/add to basket/i.test(button.textContent || '')) return;
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.dataset.tswCtsLocked = '1';
    });
  };

  const blockInvalidDims = (event) => {
    const dims = readDims();
    if (dims.ok) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    setStatus(dims.message);
    return true;
  };

  if (form) {
    form.addEventListener(
      'submit',
      (event) => {
        if (blockInvalidDims(event)) return;
        // Do not set submitting here — click guard owns the lock so OPC submit can complete.
      },
      true
    );
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target && event.target.closest && event.target.closest('button, [role="button"]');
      if (!button || !/add to basket/i.test(button.textContent || '')) return;
      if (submitting || button.dataset.tswCtsLocked === '1') {
        event.preventDefault();
        event.stopImmediatePropagation();
        setStatus('Adding to basket… please wait.');
        return;
      }
      if (blockInvalidDims(event)) return;
      // Allow this click/submit through; lock only subsequent clicks.
      submitting = true;
      window.setTimeout(() => {
        lockAddButtons();
      }, 100);
      window.setTimeout(() => {
        submitting = false;
      }, LOCK_MS);
    },
    true
  );
}

initCutToSizePaymentGuards();

function initScrollableTables() {
  const tables = document.querySelectorAll('.guide__content table, .rte table');

  tables.forEach((table) => {
    const parent = table.parentElement;
    if (!parent || parent.classList.contains('table-scroll')) return;

    // A table that scrolls horizontally is unreachable by keyboard unless the
    // scroll container is focusable and named.
    const wrapper = document.createElement('div');
    wrapper.className = 'table-scroll';
    wrapper.setAttribute('tabindex', '0');
    wrapper.setAttribute('role', 'region');

    const caption = table.querySelector('caption');
    const label = caption ? caption.textContent.trim() : '';
    wrapper.setAttribute('aria-label', label || 'Table');

    parent.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });
}

initScrollableTables();

function initAradaCollectionFinder() {
  const input = document.querySelector('[data-arada-model-search]');
  if (!input) return;

  const cards = Array.from(document.querySelectorAll('[data-arada-card]'));
  const countEl = document.querySelector('[data-arada-model-count]');
  const groups = Array.from(document.querySelectorAll('[data-arada-group]'));
  const featured = document.querySelector('[data-arada-featured]');

  const compact = (s) =>
    (s || '')
      .toLowerCase()
      .replace(/×/g, 'x')
      .replace(/mm/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/ x /g, 'x')
      .replace(/\s+/g, '');

  const applyFilter = () => {
    const raw = (input.value || '').trim();
    const q = raw.toLowerCase().replace(/×/g, 'x').replace(/\s+/g, ' ');
    const qCompact = compact(raw);
    let shown = 0;

    cards.forEach((card) => {
      const hayRaw = card.getAttribute('data-arada-search') || card.textContent || '';
      const hay = hayRaw.toLowerCase().replace(/×/g, 'x').replace(/\s+/g, ' ');
      const match = !q || hay.includes(q) || (qCompact.length >= 3 && compact(hayRaw).includes(qCompact));
      card.hidden = !match;
      if (match) shown += 1;
    });

    groups.forEach((group) => {
      const visible = group.querySelectorAll('[data-arada-card]:not([hidden])').length;
      group.dataset.aradaEmpty = visible === 0 ? 'true' : 'false';
    });

    if (featured) {
      const visible = featured.querySelectorAll('[data-arada-card]:not([hidden])').length;
      featured.dataset.aradaEmpty = visible === 0 ? 'true' : 'false';
    }

    document.querySelectorAll('[data-arada-subgroup]').forEach((wrap) => {
      const visible = wrap.querySelectorAll('[data-arada-card]:not([hidden])').length;
      wrap.hidden = visible === 0;
    });

    if (countEl) {
      if (!q) {
        countEl.hidden = true;
        countEl.textContent = '';
      } else {
        countEl.hidden = false;
        countEl.textContent = shown === 1 ? '1 matching product' : `${shown} matching products`;
      }
    }
  };

  document.querySelectorAll('[data-arada-jump-query]').forEach((link) => {
    link.addEventListener('click', () => {
      const query = link.getAttribute('data-arada-jump-query') || '';
      input.value = query;
      applyFilter();
    });
  });

  document.querySelectorAll('[data-arada-jump-clear]').forEach((link) => {
    link.addEventListener('click', () => {
      input.value = '';
      applyFilter();
    });
  });

  input.addEventListener('input', applyFilter);
  applyFilter();
}

initAradaCollectionFinder();

function initConfigStoveRope() {
  const root = document.querySelector('[data-rope-builder]');
  const form = document.querySelector('[data-product-form]');
  if (!root || !form) return;

  const jsonEl = form.querySelector('[data-product-json]');
  const variantInput = form.querySelector('[data-product-variant]');
  const selectedEl = root.querySelector('[data-rope-selected]');
  const selects = [...root.querySelectorAll('[data-rope-option]')];
  if (!jsonEl || !variantInput || selects.length < 3) return;

  let variants;
  try {
    variants = JSON.parse(jsonEl.textContent);
  } catch (error) {
    return;
  }
  if (!Array.isArray(variants)) return;

  const optionKey = (index) => `option${index}`;

  const currentValues = () => {
    const values = {};
    selects.forEach((select) => {
      values[optionKey(select.getAttribute('data-rope-option'))] = select.value;
    });
    return values;
  };

  const findVariant = (values) =>
    variants.find(
      (entry) =>
        entry.option1 === values.option1 &&
        entry.option2 === values.option2 &&
        entry.option3 === values.option3
    );

  const availableFor = (position, values) => {
    const key = optionKey(position);
    const seen = [];
    variants.forEach((entry) => {
      if (position >= 2 && entry.option1 !== values.option1) return;
      if (position >= 3 && entry.option2 !== values.option2) return;
      if (entry[key] && !seen.includes(entry[key])) seen.push(entry[key]);
    });
    return seen;
  };

  const rebuildSelect = (select, allowed, preferred) => {
    const current = preferred && allowed.includes(preferred) ? preferred : allowed[0] || '';
    [...select.options].forEach((option) => {
      option.hidden = !allowed.includes(option.value);
      option.disabled = option.hidden;
    });
    if (current) select.value = current;
  };

  const apply = () => {
    const values = currentValues();
    const diameterAllowed = [...new Set(variants.map((entry) => entry.option1).filter(Boolean))];
    rebuildSelect(selects[0], diameterAllowed, values.option1);
    values.option1 = selects[0].value;

    const lengthAllowed = availableFor(2, values);
    rebuildSelect(selects[1], lengthAllowed, values.option2);
    values.option2 = selects[1].value;

    const glueAllowed = availableFor(3, values);
    rebuildSelect(selects[2], glueAllowed, values.option3);
    values.option3 = selects[2].value;

    const variant = findVariant(values);
    if (!variant) return;

    if (String(variantInput.value) !== String(variant.id)) {
      variantInput.value = String(variant.id);
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (selectedEl) {
      selectedEl.textContent = `${variant.option1} · ${variant.option2} · ${variant.option3}`;
    }
  };

  selects.forEach((select) => {
    select.addEventListener('change', apply);
  });
  apply();
}

initConfigStoveRope();

function initSquareCoalsRange() {
  document.querySelectorAll('[data-sq-range]').forEach(initOneCoalFamilyRange);
}

function initOneCoalFamilyRange(root) {
  if (!root) return;

  const hero = root.querySelector('[data-sq-hero]');
  const form = root.querySelector('[data-sq-cart-form]');
  const variantInput = root.querySelector('[data-sq-variant-id]');
  const priceEl = root.querySelector('[data-sq-price]');
  const nameEl = root.querySelector('[data-sq-selected-name]');
  const qtyBlock = root.querySelector('[data-sq-qty-block]');
  const addButton = root.querySelector('[data-sq-add]');
  const status = root.querySelector('[data-sq-status]');
  const sizeHint = root.querySelector('[data-sq-size-hint]');
  const typeGalleries = root.querySelectorAll('[data-sq-type-gallery]');
  const moneyFormat = root.getAttribute('data-money-format') || '';
  if (!hero || !form || !variantInput || !addButton) return;

  const resetTypeGallery = (sizeKey) => {
    typeGalleries.forEach((gallery) => {
      const match = gallery.getAttribute('data-sq-type-gallery') === sizeKey;
      gallery.hidden = !match;
      if (!match) return;
      const thumbs = gallery.querySelectorAll('[data-sq-gallery-img]');
      thumbs.forEach((thumb, index) => {
        thumb.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');
      });
      const first = thumbs[0];
      if (first) {
        const src = first.getAttribute('data-sq-gallery-img');
        const alt = first.getAttribute('data-sq-gallery-alt') || '';
        if (src) {
          hero.src = src;
          hero.alt = alt;
        }
      }
    });
  };

  const resetPacks = () => {
    root.querySelectorAll('[data-sq-pack]').forEach((btn) => {
      btn.setAttribute('aria-pressed', 'false');
    });
    variantInput.value = '';
    addButton.disabled = true;
    addButton.setAttribute('aria-disabled', 'true');
  };

  const showPacksFor = (sizeKey) => {
    root.querySelectorAll('[data-sq-packs]').forEach((panel) => {
      panel.hidden = panel.getAttribute('data-sq-packs') !== sizeKey;
    });
  };

  root.querySelectorAll('[data-sq-size]').forEach((btn) => {
    btn.addEventListener('click', () => {
      root.querySelectorAll('[data-sq-size]').forEach((other) => {
        other.setAttribute('aria-pressed', other === btn ? 'true' : 'false');
      });
      const image = btn.getAttribute('data-sq-image');
      const alt = btn.getAttribute('data-sq-alt') || '';
      if (image) {
        hero.src = image;
        hero.alt = alt;
      }
      resetTypeGallery(btn.getAttribute('data-sq-size'));
      if (nameEl) {
        nameEl.textContent = btn.getAttribute('data-sq-name') || '';
        nameEl.hidden = false;
      }
      if (priceEl) {
        priceEl.textContent = btn.getAttribute('data-sq-from-label') || priceEl.textContent;
      }
      showPacksFor(btn.getAttribute('data-sq-size'));
      if (qtyBlock) qtyBlock.hidden = false;
      if (sizeHint) sizeHint.hidden = true;
      resetPacks();
      if (status) status.textContent = '';
    });
  });

  typeGalleries.forEach((gallery) => {
    gallery.querySelectorAll('[data-sq-gallery-img]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.getAttribute('data-sq-gallery-img');
        const alt = thumb.getAttribute('data-sq-gallery-alt') || '';
        if (src) {
          hero.src = src;
          hero.alt = alt;
        }
        gallery.querySelectorAll('[data-sq-gallery-img]').forEach((other) => {
          other.setAttribute('aria-pressed', other === thumb ? 'true' : 'false');
        });
      });
    });
  });

  root.querySelectorAll('[data-sq-pack]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (btn.disabled) return;
      const panel = btn.closest('[data-sq-packs]');
      if (!panel) return;
      panel.querySelectorAll('[data-sq-pack]').forEach((other) => {
        other.setAttribute('aria-pressed', other === btn ? 'true' : 'false');
      });
      variantInput.value = btn.getAttribute('data-sq-pack') || '';
      const cents = Number(btn.getAttribute('data-sq-pack-price'));
      if (priceEl && Number.isFinite(cents)) {
        priceEl.textContent = formatMoney(cents, moneyFormat);
      }
      addButton.disabled = false;
      addButton.setAttribute('aria-disabled', 'false');
      if (status) status.textContent = '';
    });
  });

  form.addEventListener('submit', (event) => {
    if (!variantInput.value) {
      event.preventDefault();
      if (status) {
        status.textContent = root.getAttribute('data-sq-need') || 'Choose a size and pack amount first.';
      }
    }
  });
}

initSquareCoalsRange();
initGasPebblesShop();

function initGasPebblesShop() {
  document.querySelectorAll('[data-pebble-shop]').forEach(initOnePebbleSizeShop);
}

function initOnePebbleSizeShop(root) {
  if (!root) return;

  const form = root.querySelector('[data-pebble-form]');
  const variantInput = root.querySelector('[data-pebble-variant]');
  const imageEl = root.querySelector('[data-pebble-image]');
  const dimEl = root.querySelector('[data-pebble-dim]');
  const priceEl = root.querySelector('[data-pebble-price]');
  const addButton = root.querySelector('[data-pebble-add]');
  const status = root.querySelector('[data-pebble-status]');
  const moneyFormat = root.getAttribute('data-money-format') || '';
  const fallbackSrc = root.getAttribute('data-pebble-fallback') || '';
  if (!form || !variantInput || !addButton) return;

  let lastGoodSrc = (imageEl && imageEl.getAttribute('src')) || fallbackSrc;

  const setImage = (src, alt) => {
    if (!imageEl) return;
    const next = src || lastGoodSrc || fallbackSrc;
    if (!next) {
      imageEl.removeAttribute('src');
      imageEl.hidden = true;
      return;
    }
    imageEl.hidden = false;
    imageEl.alt = alt || '';
    if (imageEl.getAttribute('src') !== next) {
      imageEl.src = next;
    }
  };

  if (imageEl) {
    imageEl.addEventListener('load', () => {
      if (imageEl.currentSrc || imageEl.src) lastGoodSrc = imageEl.src;
    });
    imageEl.addEventListener('error', () => {
      const safe = lastGoodSrc && lastGoodSrc !== imageEl.src ? lastGoodSrc : fallbackSrc;
      if (safe && imageEl.src !== safe) {
        imageEl.src = safe;
        return;
      }
      imageEl.removeAttribute('src');
      imageEl.hidden = true;
    });
  }

  root.querySelectorAll('.gas-pebbles-size__colour img').forEach((thumb) => {
    thumb.addEventListener('error', () => {
      if (fallbackSrc) {
        thumb.src = fallbackSrc;
        return;
      }
      thumb.remove();
    });
  });

  const selectPack = (btn, panel) => {
    if (!btn || btn.disabled) return false;
    panel.querySelectorAll('[data-pebble-pack]').forEach((other) => {
      other.setAttribute('aria-pressed', other === btn ? 'true' : 'false');
    });
    variantInput.value = btn.getAttribute('data-pebble-pack') || '';
    const cents = Number(btn.getAttribute('data-pebble-pack-price'));
    if (priceEl && Number.isFinite(cents)) {
      priceEl.textContent = formatMoney(cents, moneyFormat);
    }
    addButton.disabled = false;
    addButton.setAttribute('aria-disabled', 'false');
    return true;
  };

  const selectDefaultPack = (panel) => {
    if (!panel) return;
    const packs = [...panel.querySelectorAll('[data-pebble-pack]')];
    const ten = packs.find((btn) => !btn.disabled && btn.getAttribute('data-pebble-pack-qty') === '10');
    const first = packs.find((btn) => !btn.disabled);
    if (!selectPack(ten || first, panel)) {
      variantInput.value = '';
      addButton.disabled = true;
      addButton.setAttribute('aria-disabled', 'true');
    }
  };

  const applyColour = (btn) => {
    if (!btn) return;
    root.querySelectorAll('[data-pebble-colour]').forEach((other) => {
      other.setAttribute('aria-pressed', other === btn ? 'true' : 'false');
    });
    setImage(btn.getAttribute('data-pebble-image-src') || '', btn.getAttribute('data-pebble-image-alt') || '');
    if (dimEl) {
      const dimSrc = btn.getAttribute('data-pebble-dim-src') || '';
      if (dimSrc) {
        dimEl.src = dimSrc;
        dimEl.hidden = false;
      } else {
        dimEl.removeAttribute('src');
        dimEl.hidden = true;
      }
    }
    const handle = btn.getAttribute('data-pebble-colour');
    let activePanel = null;
    root.querySelectorAll('[data-pebble-packs]').forEach((panel) => {
      const match = panel.getAttribute('data-pebble-packs') === handle;
      panel.hidden = !match;
      if (match) activePanel = panel;
    });
    selectDefaultPack(activePanel);
    if (status) status.textContent = '';
  };

  root.querySelectorAll('[data-pebble-colour]').forEach((btn) => {
    btn.addEventListener('click', () => applyColour(btn));
  });

  root.querySelectorAll('[data-pebble-pack]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const panel = btn.closest('[data-pebble-packs]');
      if (!panel) return;
      selectPack(btn, panel);
      if (status) status.textContent = '';
    });
  });

  form.addEventListener('submit', (event) => {
    if (!variantInput.value) {
      event.preventDefault();
      if (status) status.textContent = 'Choose a colour and pack quantity first.';
    }
  });

  applyColour(root.querySelector('[data-pebble-colour]'));
}
